import { StrictMode, Component, lazy, Suspense } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { ApolloProvider } from "@apollo/client/react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { AppCrashFallback } from "./components/feedback/AppCrashFallback";
import { ToastProvider } from "./components/feedback/ToastProvider";
import { client } from "./graphql/apollo.ts";
import "./index.css";
import HomePage from "./components/HomePage.tsx";
import { Spinner } from "@/components/ui/spinner";
import { DebugRouteGuard } from "@/components/DebugRouteGuard";
import { ConnectionProvider } from "@/hooks/connectionProvider";
import { applyThemePreference, isThemePreference, readThemePreference } from "@/lib/theme";
import { initAnalytics } from "@/lib/analytics";
import { getOAuthCallbackPath } from "@/lib/oauthFlow";
import { ScrollToTop } from "@/components/ScrollToTop";
import { scrubSentryEvent } from "@shared/sentryScrubber";
import { installAppVersionRecovery } from "@/lib/appVersion";

// App is the authenticated runtime (/app). Lazy-loaded so AppRuntime and its
// heavy grid/table components stay out of the landing page's eager bundle.
const App = lazy(() => import("./App.tsx"));
const DesignSystemShowcase = lazy(() => import("./components/DesignSystemShowcase.tsx"));
const LoginPage = lazy(() => import("./components/LoginPage.tsx"));
const DemoEntry = lazy(() => import("./components/DemoEntry.tsx"));
const NotFoundPage = lazy(() => import("./components/NotFoundPage.tsx"));
const SecurityPage = lazy(() => import("./components/legal/SecurityPage.tsx"));
const PrivacyPage = lazy(() => import("./components/legal/PrivacyPage.tsx"));
const TermsPage = lazy(() => import("./components/legal/TermsPage.tsx"));
const HelpPage = lazy(() => import("./components/HelpPage.tsx"));
const PricingPage = lazy(() => import("./components/PricingPage.tsx"));
const RoadmapPage = lazy(() => import("./components/RoadmapPage.tsx"));
const AuthCallbackPage = lazy(() => import("./components/AuthCallbackPage.tsx"));
const AuthDebugPage = lazy(() => import("./components/AuthDebugPage.tsx"));
const DdbCacheDebugPage = lazy(() => import("./components/DdbCacheDebugPage.tsx"));
const ComparePerformanceDebugPage = lazy(
  () => import("./components/ComparePerformanceDebugPage.tsx")
);
const CompareStreamDebugPage = lazy(() => import("./components/CompareStreamDebugPage.tsx"));

const sentryEnvironment = import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE;
const sentryDsn = import.meta.env.VITE_SENTRY_DSN?.trim();
const sentryRelease = import.meta.env.VITE_APP_VERSION;
const sentryEnabled =
  Boolean(sentryDsn) && (sentryEnvironment === "production" || sentryEnvironment === "staging");
const chunkReloadCountKey = "dc_chunk_reload_count";
const chunkReloadAtKey = "dc_chunk_reload_at";
// A tab can sit open for hours/days across unrelated deploys. A plain one-shot
// guard exhausts itself on the FIRST chunk failure a tab ever sees and then
// silently stops recovering for the rest of its session — which is exactly
// what happened in production (P-089): a tab open since before a deploy hit a
// stale-chunk failure, got no auto-reload, and users saw the crash screen.
// Windowing the guard by elapsed time fixes that without reintroducing the
// failure mode the original one-shot guard was built to prevent: retries
// within the window are capped (no infinite reload loop while a deploy is
// still propagating), but a failure outside the window is treated as a new
// incident with a fresh budget.
const chunkReloadMaxAttempts = 2;
const chunkReloadResetWindowMs = 10 * 60 * 1000; // 10 minutes — comfortably outlasts CDN propagation

function readChunkReloadState(): { count: number; at: number } {
  try {
    const count = Number.parseInt(sessionStorage.getItem(chunkReloadCountKey) ?? "0", 10);
    const at = Number.parseInt(sessionStorage.getItem(chunkReloadAtKey) ?? "0", 10);
    return { count: Number.isFinite(count) ? count : 0, at: Number.isFinite(at) ? at : 0 };
  } catch {
    return { count: 0, at: 0 };
  }
}

function writeChunkReloadState(count: number, at: number): boolean {
  try {
    sessionStorage.setItem(chunkReloadCountKey, String(count));
    sessionStorage.setItem(chunkReloadAtKey, String(at));
    return true;
  } catch {
    // Without a cross-reload guard, reloading would loop forever.
    return false;
  }
}

function clearChunkReloadState(): void {
  try {
    sessionStorage.removeItem(chunkReloadCountKey);
    sessionStorage.removeItem(chunkReloadAtKey);
  } catch {
    // ignore — nothing to clear if storage is unavailable
  }
}

// Reset the chunk-reload budget once the app has booted and stayed up for a grace
// window with no further preloadError. Without a reset-on-success, a SUCCESSFUL recovery
// leaves the budget spent, so the next (possibly unrelated) crash within the 10-min
// window is misreported as a deploy-update screen with no retry button (P-164). A new
// preloadError during the grace window re-stamps `at`, so an ongoing incident is preserved.
function scheduleChunkReloadReset(): void {
  const graceMs = 10_000;
  const { at: atAtBoot } = readChunkReloadState();
  window.setTimeout(() => {
    const { at: atNow } = readChunkReloadState();
    if (atNow === atAtBoot) {
      clearChunkReloadState();
    }
  }, graceMs);
}

type ThemePreference = "light" | "dark" | "system";
type ThemeMode = "light" | "dark";

declare global {
  interface Window {
    __DEPLOYCOMPARE_THEME__?: {
      storageKey?: string;
      preference?: ThemePreference;
      theme?: ThemeMode;
    };
  }
}

function initializeTheme() {
  const bootstrap = window.__DEPLOYCOMPARE_THEME__;
  const preference = isThemePreference(bootstrap?.preference)
    ? bootstrap.preference
    : readThemePreference();
  if (bootstrap?.theme === "light" || bootstrap?.theme === "dark") {
    applyThemePreference(bootstrap.theme);
  } else {
    applyThemePreference(preference);
  }
}

function installChunkLoadRecovery() {
  window.addEventListener("vite:preloadError", (event) => {
    const now = Date.now();
    const { count, at } = readChunkReloadState();
    const withinSameIncident = at > 0 && now - at < chunkReloadResetWindowMs;
    const attemptsSoFar = withinSameIncident ? count : 0;

    if (attemptsSoFar >= chunkReloadMaxAttempts) {
      return;
    }
    if (!writeChunkReloadState(attemptsSoFar + 1, now)) return;
    event.preventDefault();
    const shouldReload = window.dispatchEvent(
      new Event("deploycompare:chunk-reload", { cancelable: true })
    );
    if (shouldReload) {
      window.location.reload();
    }
  });
}

function hasChunkReloadBudgetExhausted(): boolean {
  const { count, at } = readChunkReloadState();
  return count >= chunkReloadMaxAttempts && at > 0 && Date.now() - at < chunkReloadResetWindowMs;
}

// Fallback error boundary used when Sentry is unavailable
type FallbackProps = {
  fallback: (props: { eventId?: string; resetError: () => void }) => ReactNode;
  children: ReactNode;
};
type FallbackState = { eventId?: string; hasError: boolean };

let sentryClientPromise: Promise<typeof import("@sentry/react") | null> | null = null;
let sentryInitialized = false;

function loadSentryClient(): Promise<typeof import("@sentry/react") | null> {
  if (!sentryEnabled) {
    return Promise.resolve(null);
  }
  if (!sentryClientPromise) {
    sentryClientPromise = import("@sentry/react")
      .then((Sentry) => {
        if (!sentryInitialized) {
          Sentry.init({
            dsn: sentryDsn,
            environment: sentryEnvironment,
            release: sentryRelease,
            sampleRate: 1.0,
            sendDefaultPii: false,
            beforeSend: scrubSentryEvent,
            // Apollo's BaseHttpLink finalizer aborts the in-flight fetch when a
            // @stream subscription is superseded (e.g. switching jobFamily mid-
            // poll). Chrome rejects the streaming-body read; the rejection is
            // unhandled inside Apollo's multipart reader and reaches the global
            // handler. Intentional cancellation — never actionable.
            ignoreErrors: ["BodyStreamBuffer was aborted"],
          });
          sentryInitialized = true;
        }
        return Sentry;
      })
      .catch(() => null);
  }
  return sentryClientPromise;
}

class FallbackErrorBoundary extends Component<FallbackProps, FallbackState> {
  state: FallbackState = { hasError: false };

  static getDerivedStateFromError(): FallbackState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Uncaught application error:", error, info);
    void loadSentryClient().then((Sentry) => {
      if (!Sentry) return;
      const eventId = Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: info.componentStack,
          },
        },
      });
      if (eventId) {
        this.setState({ eventId });
      }
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback({
        eventId: this.state.eventId,
        resetError: () => this.setState({ eventId: undefined, hasError: false }),
      });
    }
    return this.props.children;
  }
}

// ── Async initialization (runs before render, does not block it) ────────

// Dynamically import Sentry to keep it out of the critical vendor chunk. The
// app renders immediately with the local boundary; crashes still lazy-load
// Sentry and populate the fallback support ID.
void loadSentryClient();

// ── Synchronous render ──────────────────────────────────────────────────

initializeTheme();
installChunkLoadRecovery();
if (import.meta.env.PROD) {
  installAppVersionRecovery(sentryRelease);
}
// After a clean boot, release the chunk-reload budget so a successful recovery doesn't
// leave later, unrelated crashes stuck on the retry-less "update" screen (P-164).
scheduleChunkReloadReset();

// Load PostHog after first paint — its bundle is fetched lazily and must not
// block LCP. usePostHog() in the (lazy) app routes resolves to the same global
// singleton this initializes, so no provider is needed.
initAnalytics();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ToastProvider>
      <ApolloProvider client={client}>
        <FallbackErrorBoundary
          fallback={({ eventId, resetError }) => (
            <AppCrashFallback
              eventId={eventId}
              variant={hasChunkReloadBudgetExhausted() ? "update" : "crash"}
              onRetry={() => {
                resetError();
                window.location.reload();
              }}
            />
          )}
        >
          <BrowserRouter>
            <ScrollToTop />
            <Suspense
              fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spinner />
                </div>
              }
            >
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/app" element={<App />} />
                <Route path="/app/settings/*" element={<App />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/demo" element={<DemoEntry />} />
                <Route path="/security" element={<SecurityPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/help" element={<HelpPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/roadmap" element={<RoadmapPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route path="/oauth/callback" element={<AuthCallbackPage />} />
                <Route path="/auth/github/callback" element={<AuthCallbackPage />} />
                <Route path={getOAuthCallbackPath()} element={<AuthCallbackPage />} />
                <Route
                  path="/auth-debug"
                  element={
                    <DebugRouteGuard access="authDebug">
                      <AuthDebugPage />
                    </DebugRouteGuard>
                  }
                />
                <Route
                  path="/ddb-cache-debug"
                  element={
                    <DebugRouteGuard access="ddbCacheDebug">
                      <DdbCacheDebugPage />
                    </DebugRouteGuard>
                  }
                />
                <Route
                  path="/compare-performance-debug"
                  element={
                    <DebugRouteGuard access="comparePerformanceDebug">
                      {/* ConnectionProvider so the page's compare probes carry a
                          per-operation viewKey instead of ambient fallback. */}
                      <ConnectionProvider>
                        <ComparePerformanceDebugPage />
                      </ConnectionProvider>
                    </DebugRouteGuard>
                  }
                />
                <Route
                  path="/compare-stream-debug"
                  element={
                    <DebugRouteGuard access="compareStreamDebug">
                      <ConnectionProvider>
                        <CompareStreamDebugPage />
                      </ConnectionProvider>
                    </DebugRouteGuard>
                  }
                />
                <Route
                  path="/design-system"
                  element={
                    <DebugRouteGuard access="designSystem">
                      <DesignSystemShowcase />
                    </DebugRouteGuard>
                  }
                />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </FallbackErrorBoundary>
      </ApolloProvider>
    </ToastProvider>
  </StrictMode>
);
